import { CSSProperties } from 'react';

interface OrnamentProps {
  color?: string;
  size?: number;
  style?: CSSProperties;
  className?: string;
}

/**
 * Decorative ornaments inspired by dark fantasy / Obsidian Court aesthetic
 * Scrollwork, flourishes, and frame elements
 */

// Corner flourish - for panel corners
export function CornerFlourish({
  color = 'var(--color-accent)',
  size = 32,
  style,
  className
}: OrnamentProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      style={style}
      className={className}
    >
      <path
        d="M2 30 Q2 2 30 2"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M6 30 Q6 6 30 6"
        stroke={color}
        strokeWidth="1"
        opacity="0.6"
        fill="none"
      />
      {/* Decorative curl */}
      <path
        d="M2 30 C2 24 4 20 8 18 C6 16 4 12 6 8 C8 4 14 4 16 8 C14 10 12 12 14 16"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.8"
      />
      {/* Inner detail */}
      <circle cx="10" cy="14" r="1.5" fill={color} opacity="0.6"/>
      <circle cx="4" cy="22" r="1" fill={color} opacity="0.4"/>
    </svg>
  );
}

// Horizontal divider with sword motif
export function SwordDivider({
  color = 'var(--color-accent)',
  size = 200,
  style,
  className
}: OrnamentProps) {
  const height = size * 0.15;
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 200 30"
      fill="none"
      style={style}
      className={className}
    >
      {/* Left flourish */}
      <path
        d="M10 15 Q20 10 40 15 Q30 12 20 15"
        stroke={color}
        strokeWidth="1"
        fill="none"
      />
      <path
        d="M0 15 L40 15"
        stroke={color}
        strokeWidth="0.5"
        opacity="0.4"
      />

      {/* Center sword blade */}
      <path
        d="M40 15 L95 15 L100 12 L105 15 L160 15"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
      />
      {/* Sword guard */}
      <rect x="96" y="10" width="8" height="10" rx="1" fill={color} opacity="0.3"/>
      <line x1="100" y1="8" x2="100" y2="22" stroke={color} strokeWidth="1"/>

      {/* Right flourish */}
      <path
        d="M190 15 Q180 10 160 15 Q170 12 180 15"
        stroke={color}
        strokeWidth="1"
        fill="none"
      />
      <path
        d="M200 15 L160 15"
        stroke={color}
        strokeWidth="0.5"
        opacity="0.4"
      />

      {/* Decorative dots */}
      <circle cx="50" cy="15" r="1.5" fill={color} opacity="0.6"/>
      <circle cx="150" cy="15" r="1.5" fill={color} opacity="0.6"/>
    </svg>
  );
}

// Ornate frame border - for panels
export function OrnateFrame({
  color = 'var(--color-accent)',
  style,
  className,
  children
}: OrnamentProps & { children?: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'relative',
        ...style
      }}
      className={className}
    >
      {/* Corner ornaments */}
      <CornerFlourish
        color={color}
        size={24}
        style={{ position: 'absolute', top: -2, left: -2 }}
      />
      <CornerFlourish
        color={color}
        size={24}
        style={{ position: 'absolute', top: -2, right: -2, transform: 'scaleX(-1)' }}
      />
      <CornerFlourish
        color={color}
        size={24}
        style={{ position: 'absolute', bottom: -2, left: -2, transform: 'scaleY(-1)' }}
      />
      <CornerFlourish
        color={color}
        size={24}
        style={{ position: 'absolute', bottom: -2, right: -2, transform: 'scale(-1)' }}
      />
      {children}
    </div>
  );
}

// Scrollwork header decoration
export function ScrollHeader({
  color = 'var(--color-accent)',
  size = 160,
  style,
  className
}: OrnamentProps) {
  const height = size * 0.25;
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 160 40"
      fill="none"
      style={style}
      className={className}
    >
      {/* Central motif - dragon/serpent inspired */}
      <path
        d="M80 8
           C70 8 60 12 55 20
           C50 28 55 35 65 35
           C55 30 55 22 60 18
           C65 14 75 14 80 20"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
      />
      <path
        d="M80 8
           C90 8 100 12 105 20
           C110 28 105 35 95 35
           C105 30 105 22 100 18
           C95 14 85 14 80 20"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
      />

      {/* Outer curls */}
      <path
        d="M55 20 C45 15 35 18 30 25 C25 32 30 38 40 36"
        stroke={color}
        strokeWidth="1"
        opacity="0.7"
        fill="none"
      />
      <path
        d="M105 20 C115 15 125 18 130 25 C135 32 130 38 120 36"
        stroke={color}
        strokeWidth="1"
        opacity="0.7"
        fill="none"
      />

      {/* Far extensions */}
      <path
        d="M30 25 L10 25"
        stroke={color}
        strokeWidth="0.5"
        opacity="0.4"
      />
      <path
        d="M130 25 L150 25"
        stroke={color}
        strokeWidth="0.5"
        opacity="0.4"
      />

      {/* Accent gems */}
      <circle cx="80" cy="20" r="3" fill={color} opacity="0.8"/>
      <circle cx="80" cy="20" r="1.5" fill="var(--color-background)"/>
    </svg>
  );
}

// Rune-style decoration band
export function RuneBand({
  color = 'var(--color-accent)',
  size = 200,
  style,
  className
}: OrnamentProps) {
  const height = size * 0.1;
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 200 20"
      fill="none"
      style={style}
      className={className}
    >
      {/* Base lines */}
      <line x1="0" y1="10" x2="200" y2="10" stroke={color} strokeWidth="0.5" opacity="0.3"/>
      <line x1="0" y1="5" x2="200" y2="5" stroke={color} strokeWidth="0.5" opacity="0.2"/>
      <line x1="0" y1="15" x2="200" y2="15" stroke={color} strokeWidth="0.5" opacity="0.2"/>

      {/* Rune-like marks */}
      {[20, 50, 80, 120, 150, 180].map((x, i) => (
        <g key={i} opacity={0.6 + (i % 2) * 0.2}>
          <line x1={x} y1="3" x2={x} y2="17" stroke={color} strokeWidth="1"/>
          <line x1={x-3} y1="10" x2={x+3} y2="10" stroke={color} strokeWidth="1"/>
          {i % 2 === 0 && (
            <circle cx={x} cy="10" r="2" fill="none" stroke={color} strokeWidth="0.5"/>
          )}
        </g>
      ))}
    </svg>
  );
}

// Dragon/serpent silhouette for headers
export function DragonSilhouette({
  color = 'var(--color-accent)',
  size = 48,
  style,
  className
}: OrnamentProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      style={style}
      className={className}
    >
      <path
        d="M8 36
           C6 32 8 28 12 26
           C10 24 10 20 14 18
           C12 16 14 12 18 12
           L22 10 L26 8 L30 10
           C34 8 38 10 40 14
           C42 18 40 22 36 24
           C38 26 38 30 34 32
           L30 34 L26 36
           C24 38 20 38 18 36
           C16 38 12 38 8 36Z"
        fill={color}
        opacity="0.15"
      />
      <path
        d="M8 36
           C6 32 8 28 12 26
           C10 24 10 20 14 18
           C12 16 14 12 18 12
           L22 10 L26 8 L30 10
           C34 8 38 10 40 14
           C42 18 40 22 36 24
           C38 26 38 30 34 32
           L30 34 L26 36
           C24 38 20 38 18 36
           C16 38 12 38 8 36Z"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.6"
      />
      {/* Eye */}
      <circle cx="32" cy="16" r="2" fill={color} opacity="0.8"/>
      {/* Wing detail */}
      <path
        d="M20 20 C24 16 28 18 30 22"
        stroke={color}
        strokeWidth="0.5"
        opacity="0.4"
        fill="none"
      />
    </svg>
  );
}

// Simple decorative dot pattern
export function DotPattern({
  color = 'var(--color-accent)',
  size = 100,
  style,
  className
}: OrnamentProps) {
  return (
    <svg
      width={size}
      height={size * 0.1}
      viewBox="0 0 100 10"
      fill="none"
      style={style}
      className={className}
    >
      {[10, 25, 40, 50, 60, 75, 90].map((x, i) => (
        <circle
          key={i}
          cx={x}
          cy="5"
          r={i === 3 ? 2.5 : 1.5}
          fill={color}
          opacity={i === 3 ? 0.8 : 0.4}
        />
      ))}
    </svg>
  );
}

// Celtic knotwork border (Robert Gould style)
export function KnotworkBorder({
  color = 'var(--color-accent)',
  size = 200,
  style,
  className
}: OrnamentProps) {
  const height = size * 0.1;
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 200 20"
      fill="none"
      style={style}
      className={className}
    >
      {/* Repeating knotwork pattern */}
      {[0, 40, 80, 120, 160].map((x, i) => (
        <g key={i}>
          {/* Interlaced loops */}
          <path
            d={`M${x} 10
                C${x+5} 4 ${x+15} 4 ${x+20} 10
                C${x+25} 16 ${x+35} 16 ${x+40} 10`}
            stroke={color}
            strokeWidth="2"
            fill="none"
            opacity="0.8"
          />
          <path
            d={`M${x} 10
                C${x+5} 16 ${x+15} 16 ${x+20} 10
                C${x+25} 4 ${x+35} 4 ${x+40} 10`}
            stroke={color}
            strokeWidth="2"
            fill="none"
            opacity="0.5"
          />
          {/* Intersection points */}
          <circle cx={x+20} cy="10" r="2" fill={color} opacity="0.6"/>
        </g>
      ))}
    </svg>
  );
}

// Ornate panel wrapper - Gould book cover style
export function GouldPanel({
  color = 'var(--color-accent)',
  secondaryColor: _secondaryColor = 'var(--color-primary)',
  style,
  className,
  children,
  title
}: OrnamentProps & {
  children?: React.ReactNode;
  title?: string;
  secondaryColor?: string;
}) {
  return (
    <div
      style={{
        position: 'relative',
        padding: 'var(--spacing-lg)',
        background: `linear-gradient(
          180deg,
          var(--color-surface) 0%,
          var(--color-background) 100%
        )`,
        border: `2px solid ${color}`,
        borderRadius: 'var(--radius-md)',
        boxShadow: `
          inset 0 0 30px rgba(0,0,0,0.3),
          0 4px 20px rgba(0,0,0,0.4)
        `,
        ...style
      }}
      className={className}
    >
      {/* Corner ornaments */}
      <GouldCorner
        color={color}
        style={{ position: 'absolute', top: 4, left: 4 }}
      />
      <GouldCorner
        color={color}
        style={{ position: 'absolute', top: 4, right: 4, transform: 'scaleX(-1)' }}
      />
      <GouldCorner
        color={color}
        style={{ position: 'absolute', bottom: 4, left: 4, transform: 'scaleY(-1)' }}
      />
      <GouldCorner
        color={color}
        style={{ position: 'absolute', bottom: 4, right: 4, transform: 'scale(-1)' }}
      />

      {/* Title with decorative header */}
      {title && (
        <div style={{
          textAlign: 'center',
          marginBottom: 'var(--spacing-md)',
          position: 'relative'
        }}>
          <ScrollHeader color={color} size={120} style={{ margin: '0 auto' }} />
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--font-size-lg)',
            color: color,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginTop: 'var(--spacing-xs)',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)'
          }}>
            {title}
          </h3>
        </div>
      )}

      {children}
    </div>
  );
}

// Gould-style ornate corner
export function GouldCorner({
  color = 'var(--color-accent)',
  size = 28,
  style,
  className
}: OrnamentProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      fill="none"
      style={style}
      className={className}
    >
      {/* Outer curve */}
      <path
        d="M2 26 Q2 2 26 2"
        stroke={color}
        strokeWidth="2"
        fill="none"
      />
      {/* Inner curve */}
      <path
        d="M6 26 Q6 6 26 6"
        stroke={color}
        strokeWidth="1"
        opacity="0.5"
        fill="none"
      />
      {/* Decorative flourish */}
      <path
        d="M2 26 C4 20 6 16 12 14 C8 12 6 8 8 4"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        opacity="0.7"
      />
      {/* Accent dot */}
      <circle cx="10" cy="10" r="2" fill={color} opacity="0.8"/>
      <circle cx="10" cy="10" r="1" fill="var(--color-background)"/>
    </svg>
  );
}

// Glowing magical effect border
export function MagicGlow({
  color = 'var(--color-accent)',
  style,
  className
}: OrnamentProps) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: -2,
        borderRadius: 'inherit',
        background: `linear-gradient(
          45deg,
          transparent 30%,
          ${color}20 50%,
          transparent 70%
        )`,
        pointerEvents: 'none',
        animation: 'glow-rotate 8s linear infinite',
        ...style
      }}
      className={className}
    />
  );
}

// Decorative section header
export function SectionHeader({
  color = 'var(--color-accent)',
  title,
  style,
  className
}: OrnamentProps & { title: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-md)',
        marginBottom: 'var(--spacing-md)',
        ...style
      }}
      className={className}
    >
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${color}60)` }} />
      <span style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'var(--font-size-sm)',
        color: color,
        textTransform: 'uppercase',
        letterSpacing: '0.15em',
        whiteSpace: 'nowrap'
      }}>
        {title}
      </span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${color}60, transparent)` }} />
    </div>
  );
}

// Ornate Machina Branding Centerpiece
export function MachinaCenterpiece({
  color = 'var(--color-accent)',
  size = 280,
  style,
  className
}: OrnamentProps) {
  const height = size * 0.2;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        ...style
      }}
      className={className}
    >
      <svg
        width={size}
        height={height}
        viewBox="0 0 280 56"
        fill="none"
      >
        <defs>
          {/* Gold gradient for text */}
          <linearGradient id="goldTextGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f5e7b0" />
            <stop offset="30%" stopColor="#d4af37" />
            <stop offset="70%" stopColor="#b8942c" />
            <stop offset="100%" stopColor="#8b6914" />
          </linearGradient>
          {/* Subtle glow filter */}
          <filter id="textGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feFlood floodColor="#d4af37" floodOpacity="0.5" />
            <feComposite in2="blur" operator="in" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Left decorative flourish */}
        <g opacity="0.7">
          {/* Outer curve */}
          <path
            d="M10 28 Q25 8 55 20 Q70 26 85 28"
            stroke={color}
            strokeWidth="1.5"
            fill="none"
          />
          {/* Inner detail curve */}
          <path
            d="M20 28 Q32 14 50 22 Q65 27 80 28"
            stroke={color}
            strokeWidth="0.75"
            opacity="0.5"
            fill="none"
          />
          {/* Decorative spiral */}
          <path
            d="M8 28 C6 22 12 16 18 18 C22 20 20 26 16 26"
            stroke={color}
            strokeWidth="1"
            fill="none"
          />
          {/* Accent dots */}
          <circle cx="40" cy="18" r="1.5" fill={color} opacity="0.6" />
          <circle cx="60" cy="22" r="1" fill={color} opacity="0.4" />
        </g>

        {/* Center crossed swords behind text */}
        <g opacity="0.3">
          {/* Left sword */}
          <path
            d="M120 8 L130 28 L120 48 L128 28 Z"
            fill={color}
          />
          {/* Right sword */}
          <path
            d="M160 8 L150 28 L160 48 L152 28 Z"
            fill={color}
          />
          {/* Cross guard */}
          <ellipse cx="140" cy="28" rx="8" ry="3" fill={color} opacity="0.5" />
        </g>

        {/* MACHINA text */}
        <text
          x="140"
          y="38"
          textAnchor="middle"
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize="26"
          fontWeight="400"
          letterSpacing="8px"
          fill="url(#goldTextGradient)"
          filter="url(#textGlow)"
          style={{ fontVariant: 'small-caps' }}
        >
          MACHINA
        </text>

        {/* Underline flourish */}
        <path
          d="M90 46 Q140 52 190 46"
          stroke={color}
          strokeWidth="1"
          fill="none"
          opacity="0.6"
        />
        <circle cx="140" cy="50" r="2" fill={color} opacity="0.8" />

        {/* Right decorative flourish (mirrored) */}
        <g opacity="0.7" transform="translate(280, 0) scale(-1, 1)">
          {/* Outer curve */}
          <path
            d="M10 28 Q25 8 55 20 Q70 26 85 28"
            stroke={color}
            strokeWidth="1.5"
            fill="none"
          />
          {/* Inner detail curve */}
          <path
            d="M20 28 Q32 14 50 22 Q65 27 80 28"
            stroke={color}
            strokeWidth="0.75"
            opacity="0.5"
            fill="none"
          />
          {/* Decorative spiral */}
          <path
            d="M8 28 C6 22 12 16 18 18 C22 20 20 26 16 26"
            stroke={color}
            strokeWidth="1"
            fill="none"
          />
          {/* Accent dots */}
          <circle cx="40" cy="18" r="1.5" fill={color} opacity="0.6" />
          <circle cx="60" cy="22" r="1" fill={color} opacity="0.4" />
        </g>
      </svg>
    </div>
  );
}

// Rune-blade sword icon
export function BladeIcon({
  color = 'var(--color-accent)',
  size = 48,
  style,
  className
}: OrnamentProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      style={style}
      className={className}
    >
      {/* Blade */}
      <path
        d="M24 4 L26 8 L26 32 L24 36 L22 32 L22 8 Z"
        fill={color}
        opacity="0.8"
      />
      {/* Blade highlight */}
      <path
        d="M24 4 L25 8 L25 32 L24 34"
        stroke="white"
        strokeWidth="0.5"
        opacity="0.3"
        fill="none"
      />
      {/* Guard */}
      <path
        d="M16 32 L32 32 L30 34 L18 34 Z"
        fill={color}
      />
      {/* Grip */}
      <rect x="22" y="34" width="4" height="8" rx="1" fill={color} opacity="0.9"/>
      {/* Pommel */}
      <circle cx="24" cy="44" r="3" fill={color}/>
      <circle cx="24" cy="44" r="1.5" fill="var(--color-background)" opacity="0.5"/>
      {/* Runes on blade */}
      <path
        d="M24 12 L24 14 M24 18 L24 20 M24 24 L24 26"
        stroke="var(--color-background)"
        strokeWidth="1"
        opacity="0.4"
      />
      {/* Dark aura */}
      <circle cx="24" cy="24" r="20" fill="none" stroke={color} strokeWidth="0.5" opacity="0.2"/>
    </svg>
  );
}
